import {
  Component,
  AfterViewInit,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  signal,
  computed,
  effect,
  HostListener,
  inject,
  DestroyRef,
  NgZone,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthenticationService } from '@authentication';
import { Client } from '../models/client';
import { ClientsService } from '../services/clients.service';
import { sidenavAnimation } from './animation';
import { DialogService } from '@migo-apps/dialogs';
import { TranslateService } from '@ngx-translate/core';
import { SESSION_CLOSED } from '@shared/utils/i18n/constants';
import { UsersService } from '../../users/services/users.service';
import { filter } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from '../services/device-detector/device-detector.service';
import { MatDialog } from '@angular/material/dialog';
import { SearchClientDialogComponent } from './components/search-client-dialog/search-client-dialog.component';
import { getInitials } from '@shared/utils';
import { Subscription } from 'rxjs';
import { BreakpointService } from '../services/breakpoint/breakpoint.service';
import { FiltersResetService } from '../../transactions/services/filters-reset.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 *Layout principal del dashboard
 *
 * @export
 * @class MainLayoutComponent
 */
@UntilDestroy()
@Component({
  standalone: false,
  selector: 'migo-apps-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  animations: [sidenavAnimation],
})
export class MainLayoutComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('navbarRef') navbarRef!: ElementRef;
  @ViewChild('toggleButton') toggleButtonRef!: ElementRef;

  private manuallyVisible = signal(false);
  readonly isCompact = this.bp.isCompact;
  readonly isMediumOrDesktop = computed(
    () => this.bp.isMedium() || this.bp.isDesktop(),
  );

  readonly isNavbarVisible = computed(() =>
    this.isMediumOrDesktop() ? true : this.manuallyVisible(),
  );

  isMobile$ = this.deviceDetectorService.isMobile$;

  selectedClient$ = this.clientsSvc.selectedClient$;
  user = this.authSvc.getUser();
  permissionsKeys: string[] = [];
  activeButtom = 'dashboard';

  isErrorPage = false;
  isAccountPage = false;
  isPayoutPage = false

  isExpanded = false;
  contextMenuVisible = false;

  private subscription!: Subscription;

  scrollText = false;
  private isOverflowing = false;

  @ViewChild('nameContainer', { static: false })
  private nameContainer!: ElementRef<HTMLElement>;

  @ViewChild('nameText', { static: false })
  private nameText!: ElementRef<HTMLElement>;

  private destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);

  private pressTimer: any = null;
  private longPressTriggered = false;
  private readonly LONG_PRESS_MS = 500;

  constructor(
    private readonly authSvc: AuthenticationService,
    private readonly router: Router,
    private readonly clientsSvc: ClientsService,
    private readonly dialogSvc: DialogService,
    private readonly i18n: TranslateService,
    private readonly usersSvc: UsersService,
    private readonly deviceDetectorService: DeviceDetectorService,
    private dialog: MatDialog,
    public bp: BreakpointService,
    private readonly filtersResetSvc: FiltersResetService,
  ) {
    this.getPermissionsByUser();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.activeButtom = this.router.url;
      });

    effect(() => {
      if (!this.isCompact()) {
        this.manuallyVisible.set(false);
      } else {
        if (this.manuallyVisible() === true) {
          this.isExpanded = true;
        }
      }
    });
  }
  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.subscribeUserChanges();
    this.subscribeRouterEvents();
    this.filtersResetSvc.listenToNavigation();
  }

  subscribeUserChanges() {
    this.subscription = this.usersSvc.onUserChanged().subscribe((value) => {
      this.user = this.authSvc.getUser();
      this.user.avatarUrl = value ?? '';
    });
  }

  subscribeRouterEvents() {
    this.isErrorPage = this.router.url.startsWith('/error/');
    this.isAccountPage = this.router.url.startsWith('/account');
    this.isPayoutPage = this.router.url.startsWith('/payoutCards');
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.isErrorPage = event.url.startsWith('/error/');
        this.isAccountPage = event.url.startsWith('/account');
        this.isPayoutPage = event.url.startsWith('/payoutCards');
      });
  }

  /**
   * We're subscribing to the `getClients` function in the `clients.service.ts` file
   */
  ngAfterViewInit(): void {
    this.setupOverflowObservers();

    this.selectedClient$
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.raf(() => this.updateOverflowFlag()));

    const user = this.authSvc.getUser();
    this.clientsSvc
      .getClients(user.userId)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  /**
   * The function takes a client object as an argument, and then calls the
   * selectClient() function in the client service, passing the client object as an
   * argument
   * @param {Client} client - Client - this is the client object that is passed in
   * from the client-list.component.html file.
   */
  selectClient(client: Client) {
    this.clientsSvc.selectClient(client);
    this.verifyRoute(client);
  }

  /**
   * It logs the user out, and then navigates to the auth page
   */
  logout() {
    this.authSvc.logout().subscribe(() => {
      this.i18n.get(SESSION_CLOSED).subscribe((text) => {
        this.dialogSvc.openSnackbar(text);
        this.authSvc.removePermissions();
        this.router.navigate(['auth']);
      });
    });
  }

  verifyRoute(client: Client) {
    const currentPath = this.router.url;
    if (currentPath.includes('elektra') && !currentPath.includes(client.key)) {
      this.router.navigate(['dashboard']);
    }
  }

  getPermissionsByUser() {
    const userId = this.authSvc.getUser();
    this.usersSvc.getPermissionsByUser(userId.userId).subscribe({
      next: (response) => {
        this.authSvc.removePermissions();
        this.authSvc.setPermissions(JSON.stringify(response));
        for (const perm of response) {
          const findList = perm.actions.find((fd) => fd === 'list');
          if (findList) {
            this.permissionsKeys.push(perm.keyName);
          } else if (
            perm.keyName === 'createTransaction' &&
            perm.actions.length > 0
          ) {
            this.permissionsKeys.push(perm.keyName);
          }
        }
      },
      error: (err) => {
        console.error('ERROR RESPONSE GET PERMISSIONS BY USER', err);
      },
    });
  }

  checkView(keyName: string) {
    const findKey = this.permissionsKeys.find((fd) => fd === keyName);

    if (findKey) {
      return true;
    }
    return false;
  }

  navigateDashboard(metricId: number) {
    this.router.navigate(['/admin/metrics/viewer', metricId]);
  }

  navigatePage(route: string) {
    this.router.navigate(['/' + route]);
  }

  toggleContextMenu(event: Event) {
    event.stopPropagation();
    this.contextMenuVisible = !this.contextMenuVisible;
  }

  openClientSearchDialog() {
    this.dialog.open(SearchClientDialogComponent, {
      data: {
        clients: this.clientsSvc.getClientsValue(),
      },
      panelClass: 'custom-client-dialog-panel',
    });
  }

  onMouseEnter() {
    this.isExpanded = true;
  }

  onMouseLeave() {
    if (this.bp.isCompact()) return;
    this.isExpanded = false;
    if (this.contextMenuVisible) this.contextMenuVisible = false;
  }

  /* onMouseEnterScroll() {
    // Asegúrate de recalcular por si cambió el ancho
    this.updateOverflowFlag();

    if (!this.isOverflowing) {
      this.scrollText = false;
      return;
    }

    const c = this.nameContainer?.nativeElement;
    const t = this.nameText?.nativeElement;
    if (!c || !t) return;

    // Distancia a desplazar = (ancho del texto) - (ancho del contenedor)
    const distance = Math.max(0, t.scrollWidth - c.clientWidth);

    // Velocidad “natural”: ~40px/s, con límites razonables
    const pxPerSec = 40;
    const seconds = Math.min(20, Math.max(6, distance / pxPerSec));

    // Inyecta variables CSS para esta instancia
    t.style.setProperty('--scroll-distance', `${distance}px`);

    this.scrollText = true;
  } */

  onMouseEnterScroll() {
    this.updateOverflowFlag();
    if (!this.isOverflowing) {
      this.scrollText = false;
      return;
    }

    const c = this.nameContainer?.nativeElement;
    const t = this.nameText?.nativeElement;
    if (!c || !t) return;

    const cs = getComputedStyle(c);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;

    const available = c.clientWidth - padL - padR;
    const EPS = 1;
    const distance = Math.max(0, t.scrollWidth - available + EPS);

    t.style.setProperty('--scroll-distance', `${distance}px`);
    t.style.setProperty('--marquee-duration', `2000ms`);

    this.scrollText = true;
  }

  onMouseLeaveScroll() {
    this.scrollText = false;

    const t = this.nameText?.nativeElement;
    if (t) {
      t.style.removeProperty('--scroll-distance');
      t.style.removeProperty('--marquee-duration');
      // Si quieres que vuelva instantáneamente al salir, puedes forzar:
      t.style.transform = 'translateX(0)';
      setTimeout(() => t.style.removeProperty('transform'));
    }
  }

  getInitials() {
    return getInitials(this.user.name);
  }

  toggleNavbar(): void {
    this.manuallyVisible.update((v) => !v);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.bp.isCompact()) return;
    const target = event.target as HTMLElement;

    const clickedInsideNavbar =
      this.navbarRef?.nativeElement.contains(target) ?? false;

    const clickedToggleButton =
      this.toggleButtonRef?.nativeElement.contains(target) ?? false;

    if (
      this.isCompact() &&
      this.manuallyVisible() &&
      !clickedInsideNavbar &&
      !clickedToggleButton
    ) {
      this.manuallyVisible.set(false);
    }
  }

  private setupOverflowObservers() {
    const container = this.nameContainer.nativeElement;
    const text = this.nameText.nativeElement;

    const ro = new ResizeObserver(() => this.updateOverflowFlag());
    ro.observe(container);
    ro.observe(text);

    // Por si cambia el nodo de texto (async pipe o i18n)
    const mo = new MutationObserver(() => this.updateOverflowFlag());
    mo.observe(text, { childList: true, characterData: true, subtree: true });

    // Limpieza
    this.destroyRef.onDestroy(() => {
      ro.disconnect();
      mo.disconnect();
    });

    // Chequeo inicial
    this.raf(() => this.updateOverflowFlag());
  }

  /** Calcula si el contenido se desborda horizontalmente */
  private updateOverflowFlag() {
    const c = this.nameContainer?.nativeElement;
    const t = this.nameText?.nativeElement;
    if (!c || !t) return;

    const overflowing = t.scrollWidth > c.clientWidth + 1;

    if (overflowing !== this.isOverflowing) {
      this.isOverflowing = overflowing;
      if (!this.isOverflowing) this.scrollText = false;
    }
  }

  private raf(fn: () => void) {
    this.zone.runOutsideAngular(() =>
      requestAnimationFrame(() => this.zone.run(fn)),
    );
  }

  onPressStart(ev: PointerEvent) {
    // Solo nos interesa “presionado”; ignoramos secundario/scroll
    if (ev.pointerType === 'mouse') return; // en desktop ya usas mouseenter
    this.clearPressTimer();
    this.longPressTriggered = false;

    // Opcional: evita scroll accidental durante el long-press
    // ev.preventDefault();

    this.pressTimer = setTimeout(() => {
      this.longPressTriggered = true;
      this.onMouseEnterScroll(); // reutiliza tu lógica de scroll
    }, this.LONG_PRESS_MS);
  }

  onPressEnd(_ev: PointerEvent) {
    this.clearPressTimer();
    if (this.longPressTriggered) {
      // Detén el scroll cuando deja de presionar
      this.onMouseLeaveScroll();
    }
    // No resetees longPressTriggered aquí; lo usamos para suprimir el click
    // Se limpia en onSelectedClientClick
  }

  onPressCancel() {
    this.clearPressTimer();
    if (this.longPressTriggered) {
      this.onMouseLeaveScroll();
    }
  }

  private clearPressTimer() {
    if (this.pressTimer) {
      clearTimeout(this.pressTimer);
      this.pressTimer = null;
    }
  }

  /** Mantén tu comportamiento original de abrir el diálogo,
   *  pero suprime el click inmediatamente después de un long-press. */
  onSelectedClientClick(ev: MouseEvent) {
    if (this.longPressTriggered) {
      ev.stopPropagation();
      ev.preventDefault();
      this.longPressTriggered = false; // listo para el siguiente gesto
      return;
    }
    this.openClientSearchDialog();
  }
}
